import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { getSortedCountries } from '@/hooks/useGuestVerification';

/**
 * Country picker used everywhere a property's country is entered (PropertyForm,
 * the onboarding wizard). Picking from a list — instead of free text — keeps
 * the value clean for geocoding and display.
 *
 * The stored VALUE is always the English name (e.g. "Croatia") so the same
 * property reads identically regardless of who entered it; only the visible
 * label is localized. See getCountryLabel() for the reverse (display) mapping.
 */
export interface CountrySelectProps {
  value: string;
  onChange: (englishName: string) => void;
  label?: string;
  error?: string;
  id?: string;
  /** Shown as the disabled first option when nothing is selected yet. */
  placeholder?: string;
  className?: string;
}

export const CountrySelect = React.forwardRef<HTMLSelectElement, CountrySelectProps>(
  ({ value, onChange, label, error, id, placeholder, className = '' }, ref) => {
    const { t, i18n } = useTranslation();
    const generatedId = React.useId();
    const selectId = id || generatedId;
    const errorId = error ? `${selectId}-error` : undefined;
    const list = getSortedCountries(i18n.language);
    const isEn = i18n.language.startsWith('en');

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={selectId} className="block text-sm font-medium text-[hsl(var(--foreground))] mb-1.5">
            {label}
          </label>
        )}
        <select
          id={selectId}
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          className={`
            flex h-10 w-full rounded-md border px-3 py-2 text-base md:text-sm
            bg-[hsl(var(--background))]
            border-[hsl(var(--input))]
            ring-offset-[hsl(var(--background))]
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2
            disabled:cursor-not-allowed disabled:opacity-50
            ${error ? 'border-[hsl(var(--destructive))]' : ''}
            ${className}
          `}
        >
          <option value="" disabled>
            {placeholder || t('common.selectCountry', 'Odaberi državu')}
          </option>
          {list.map((c) => (
            <option key={c.code} value={c.nameEn}>
              {isEn ? c.nameEn : c.name}
            </option>
          ))}
        </select>
        {error && (
          <p id={errorId} role="alert" className="mt-1 text-sm text-[hsl(var(--destructive))]">{error}</p>
        )}
      </div>
    );
  }
);

CountrySelect.displayName = 'CountrySelect';

export default CountrySelect;
